import { AuthService } from './authService';
import { extractApiError } from '../utils/apiError';
import type { Printer, Computer } from '../types/printNode';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

/** TW 36430601: 0 = tenant, 1 = lager, 2 = profil */
export type PrinterFilterScopeType = 0 | 1 | 2;

export interface PrinterFilterRow {
  scopeType: PrinterFilterScopeType;
  companyNo?: string | null;
  warehouseID?: string | null;
  userID?: string | null;
  printNodePrinterId: number;
  displayName?: string | null;
}

export interface PrinterFilterOverview {
  /** Hele PrintNode-kontoen (ufiltrert) */
  printers: Printer[];
  computers: Computer[];
  rows: PrinterFilterRow[];
}

/**
 * TW 36430601: vedlikehold av skriverfilteret (tenant, lager, profil). Kun lagersjef/admin.
 * En tom liste på et nivå betyr at nivået arver alt fra nivået over.
 */
class PrinterFilterService {
  private url(companyNo: string, path = ''): string {
    return `${API_BASE_URL}/${companyNo}/printer-filter${path}`;
  }

  private async ensureOk(response: Response, fallback: string): Promise<void> {
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, fallback));
    }
  }

  async getOverview(companyNo: string): Promise<PrinterFilterOverview> {
    const response = await AuthService.authenticatedGet(this.url(companyNo));
    await this.ensureOk(response, 'Kunne ikke hente skriverfilteret');
    return response.json();
  }

  async saveTenant(companyNo: string, printers: Array<{ printerId: number; displayName?: string | null }>): Promise<void> {
    const response = await AuthService.authenticatedPut(this.url(companyNo, '/tenant'), { printers });
    await this.ensureOk(response, 'Kunne ikke lagre skriverne for tenanten');
  }

  async saveWarehouse(companyNo: string, warehouseId: string, printerIds: number[]): Promise<void> {
    const response = await AuthService.authenticatedPut(
      this.url(companyNo, `/warehouse/${encodeURIComponent(warehouseId)}`), { printerIds });
    await this.ensureOk(response, 'Kunne ikke lagre skriverne for lageret');
  }

  async saveProfile(companyNo: string, userId: string, printerIds: number[]): Promise<void> {
    const response = await AuthService.authenticatedPut(
      this.url(companyNo, `/profile/${encodeURIComponent(userId)}`), { printerIds });
    await this.ensureOk(response, 'Kunne ikke lagre skriverne for profilen');
  }
}

export const printerFilterService = new PrinterFilterService();
